import { computed, onMounted, ref } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'
import type { TagAdditionBulkRequest } from '@/types/external/2work/api/request/TagAdditionBulkRequest'
import type { TagAdditionResponse } from '@/types/external/2work/api/response/TagAdditionResponse'

import { DOCTYPES } from '@/constants/doctypes'
import { PERMISSIONS } from '@/constants/permissions'
import { type RelationGroupType, RelationType } from '@/constants/relations'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useAuthStore } from '@/stores/auth'

const createDropdownOptions = (tags: string[]): DropdownOption<string>[] => {
  return tags.map((tag): DropdownOption<string> => {
    return {
      optionLabel: tag,
      optionValue: tag,
    }
  })
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
const useTagOptions = (getAllOnMount = true) => {
  const customAPIStore = useFrappeNestCustomAPIStore()
  const authStore = useAuthStore()

  const canCreateTag = computed(() => {
    return authStore.hasPermission(DOCTYPES.TAG, PERMISSIONS.CREATE)
  })

  /**
   * tags specific to certain relationtypes
   */
  const filteredTagsByRelationType = ref<string[]>([])
  const filteredTagsOptions = computed(() => {
    return createDropdownOptions(filteredTagsByRelationType.value)
  })
  /**
   * Get all available tags for a given sub_type and type (filteredTags).
   * @param sub_type
   * @param type
   */
  const getTagsforTypes = async (
    sub_type: RelationType,
    type: RelationGroupType,
  ): Promise<void> => {
    const newTagList = await customAPIStore.getTagsforTypes({
      sub_type,
      type,
    })

    filteredTagsByRelationType.value = newTagList
  }

  /**
   * Get all available tags (allTags).
   */
  const allTags = ref<string[]>([])
  const allTagsOptions = computed(() => {
    return createDropdownOptions(allTags.value)
  })
  const getAllTags = async (): Promise<void> => {
    const newTagList = await customAPIStore.getAllTags()

    allTags.value = newTagList
  }

  /**
   * Hold new tags that are created at runtime and not sent to database yet.
   */
  const newTagsOptions = ref<DropdownOption<string>[]>([])
  const createNewTagOption = (labelString: string): DropdownOption<string> => {
    const newTagOption = {
      optionLabel: labelString,
      optionValue: labelString,
    }

    newTagsOptions.value.push(newTagOption)

    return newTagOption
  }

  const tagOptions = computed(() => {
    return [...allTagsOptions.value, ...newTagsOptions.value]
  })

  const addTagsToDocuments = async (
    addTagsToDocumentsObject: TagAdditionBulkRequest,
    relationGroupType?: RelationGroupType,
  ): Promise<void> => {
    try {
      const response = await customAPIStore.addTagsToDocuments(addTagsToDocumentsObject)

      const success = response.every((resp: TagAdditionResponse) => {
        return resp.message.includes('success')
      })

      if (!success) {
        throw new Error('Error adding tags')
      }
      if (relationGroupType) {
        await getTagsforTypes(RelationType.all, relationGroupType)
      }
    } catch (getError: unknown) {
      console.error(getError)
    }
  }

  const refreshTags = async (): Promise<void> => {
    await getAllTags()
    newTagsOptions.value = []
  }

  onMounted(async () => {
    if (getAllOnMount) {
      await getAllTags()
    }
  })

  return {
    addTagsToDocuments,
    allTags,
    allTagsOptions,
    canCreateTag,
    createNewTagOption,
    filteredTagsByRelationType,
    filteredTagsOptions,
    getAllTags,
    getTagsforTypes,
    refreshTags,
    tagOptions,
  }
}

export { useTagOptions }
